import { Layer, ZIndexes } from "@common/constants";
import { ArtilleryKind, type ArtilleryData } from "@common/packets/artilleryPacket";
import { FloorNames } from "@common/utils/terrain";
import { Vec, type Vector } from "@common/utils/vector";
import { Container, Graphics, GraphicsContext } from "pixi.js";
import { Game } from "../game";
import { PIXI_SCALE } from "../utils/constants";
import { TrenchioSprite, toPixiCoords } from "../utils/pixi";
import { t } from "../utils/translations/phrases";
import { CameraManager } from "./cameraManager";
import { GoreManager } from "./gore";
import { MapManager } from "./mapManager";
import { NIGHT_ZINDEX, NightLight } from "./nightLight";
import { ParticleManager } from "./particleManager";
import { SoundManager } from "./soundManager";

/** how long the whistle plays before its shell lands (the sound's length) */
const WHISTLE_MS = 2600;
/** whistles per barrage, at random spots in the zone (they don't say where the shells fall) */
const WHISTLES = 8;
/** a shell's blast radius (units; the server's ArtilleryRules.blastRadius) */
const BLAST_RADIUS = 12;
/** the bomber's shadow crosses 1400 units over this long, starting when the barrage is announced */
const SHADOW_MS = 7000;
/** decoy target markers wandering around the zone: they never show where a shell will land */
const DECOYS = 12;
/** impacts farther than this from you get no blast effects, particles or shake (just the crater) */
const EFFECT_RANGE = 150;
/** and this close, an extra jolt */
const CLOSE_RANGE = 26;

interface Decoy {
    readonly mark: Graphics
    position: Vector
    velocity: Vector
    nextTurnAt: number
    pausedUntil: number
    hiddenUntil: number
}

interface ActiveBarrage {
    readonly container: Container
    readonly zone: Graphics
    readonly decoys: Decoy[]
    readonly shadow: TrenchioSprite
    readonly start: number
    readonly landsAt: number
    readonly endsAt: number
    readonly center: Vector
    readonly radius: number
    readonly heading: number
    /** whistles still to play: when, and where */
    readonly whistles: Array<{ at: number, position: Vector }>
}

/** a shell coming in: its shadow grows under it as it streaks down */
interface Falling {
    readonly position: Vector
    readonly start: number
    readonly landsAt: number
    readonly shadow: Graphics
    readonly streak: Graphics
    /** the direction it comes in from (on screen) */
    readonly slant: number
}

/** short-lived drawings: the flash and the shockwave ring */
interface Flash {
    readonly sprite: TrenchioSprite
    readonly start: number
}
interface Ring {
    readonly ring: Graphics
    readonly start: number
}
/** the fireball, and the dark scorch puff after it */
interface Blast {
    readonly fire: TrenchioSprite
    readonly scorch: TrenchioSprite
    readonly start: number
}

const FLASH_MS = 170;
const RING_MS = 440;
const FIRE_MS = 650;
const SCORCH_MS = 1100;

/** the shapes every shell uses, built once and shared (cheap to show, even 28 at a time) */
let shapes: { readonly ring: GraphicsContext, readonly shadow: GraphicsContext, readonly streak: GraphicsContext, readonly decoy: GraphicsContext } | undefined;
const sharedShapes = (): NonNullable<typeof shapes> => shapes ??= {
    ring: new GraphicsContext().circle(0, 0, BLAST_RADIUS * PIXI_SCALE).stroke({ width: 14, color: 0xfff0d0, alpha: 1 }),
    shadow: new GraphicsContext().ellipse(0, 0, 3.2 * PIXI_SCALE, 2.2 * PIXI_SCALE).fill({ color: 0x000000, alpha: 1 }),
    streak: new GraphicsContext()
        .moveTo(0, 0).lineTo(0, -230).stroke({ width: 18, color: 0xffc27a, alpha: 0.28 })
        .moveTo(0, 0).lineTo(0, -170).stroke({ width: 6, color: 0xfff1d6, alpha: 1 })
        .circle(0, 0, 13).fill({ color: 0xffe2a8, alpha: 0.5 })
        .circle(0, 0, 8).fill({ color: 0xffffff, alpha: 1 }),
    decoy: new GraphicsContext()
        .circle(0, 0, BLAST_RADIUS * PIXI_SCALE).stroke({ width: 3, color: 0xff3b2f, alpha: 0.65 })
        .moveTo(-22, 0).lineTo(22, 0).moveTo(0, -22).lineTo(0, 22).stroke({ width: 3, color: 0xff3b2f, alpha: 0.9 })
};

const randomIn = (center: Vector, radius: number): Vector => Vec.add(center, Vec.fromPolar(Math.random() * Math.PI * 2, radius * Math.sqrt(Math.random())));

class ArtilleryClass {
    private readonly _barrages = new Map<number, ActiveBarrage>();
    private readonly _falling: Falling[] = [];
    private readonly _flashes: Flash[] = [];
    private readonly _rings: Ring[] = [];
    private readonly _blasts: Blast[] = [];
    private _craters?: Container;
    /** falling shells and flashes, above everything */
    private _sky?: Container;
    /** shockwave rings, on the ground */
    private _ground?: Container;
    private _banner?: HTMLElement;

    onPacket(data: ArtilleryData): void {
        switch (data.kind) {
            case ArtilleryKind.Announce: this._announce(data); break;
            case ArtilleryKind.Cancel: this._remove(data.id); break;
            case ArtilleryKind.Incoming: {
                const now = performance.now();
                const barrage = this._barrages.get(data.id);
                for (const shell of data.shells) this._incoming(shell.position, now, now + shell.delay, barrage?.heading ?? 0);
                break;
            }
        }
    }

    /** a barrage is coming: the zone, decoy markers wandering in it, the bomber's shadow, whistles, the banner */
    private _announce(data: ArtilleryData): void {
        this._remove(data.id);
        const now = performance.now();
        const landsAt = now + data.landsIn;
        const endsAt = landsAt + data.spread;
        // the zone and the markers, on the ground (under players and obstacles); at night over the dark, as the gas is
        // (a warning has to be seen: 1.05)
        const container = new Container();
        container.zIndex = NightLight.active ? NIGHT_ZINDEX + 1 : ZIndexes.Decals + 0.5;
        const c = toPixiCoords(data.center), r = data.radius * PIXI_SCALE;
        const zone = new Graphics()
            .circle(c.x, c.y, r).fill({ color: 0xc81e1e, alpha: 0.13 })
            .circle(c.x, c.y, r).stroke({ width: 7, color: 0xff3b2f, alpha: 0.9 });
        container.addChild(zone);
        const decoys: Decoy[] = [];
        for (let i = 0; i < DECOYS; i++) {
            const mark = new Graphics(sharedShapes().decoy);
            mark.scale.set(0.75 + Math.random() * 0.4);
            const position = randomIn(data.center, data.radius * 0.85);
            const p = toPixiCoords(position);
            mark.position.set(p.x, p.y);
            container.addChild(mark);
            decoys.push({ mark, position, velocity: Vec(0, 0), nextTurnAt: now, pausedUntil: 0, hiddenUntil: 0 });
        }
        // the bomber's shadow, above the roofs (a shadow falls on everything)
        const shadow = new TrenchioSprite("airdrop_plane");
        shadow.tint = 0x000000;
        shadow.alpha = 0;
        shadow.scale.set(4.5);
        shadow.rotation = data.heading;
        shadow.zIndex = ZIndexes.ObstaclesLayer5;
        CameraManager.addObject(container, shadow);
        SoundManager.play("airdrop_plane", { position: data.center, falloff: 0.4, maxRange: 280, layer: Layer.Ground });
        // whistles all over the zone, ending while the shells come down
        const whistles: Array<{ at: number, position: Vector }> = [];
        for (let i = 0; i < WHISTLES; i++) {
            const end = landsAt + data.spread * (i / Math.max(1, WHISTLES - 1)) + (Math.random() - 0.5) * 300;
            whistles.push({ at: Math.max(now, end - WHISTLE_MS), position: randomIn(data.center, data.radius) });
        }
        this._barrages.set(data.id, { container, zone, decoys, shadow, start: now, landsAt, endsAt, center: data.center, radius: data.radius, heading: data.heading, whistles });
        this._updateBanner(now);
    }

    private _layers(): { sky: Container, ground: Container } {
        if (!this._sky) {
            this._sky = new Container();
            this._sky.zIndex = ZIndexes.ObstaclesLayer5 + 1;
            this._ground = new Container();
            this._ground.zIndex = ZIndexes.Decals + 0.7;
            CameraManager.addObject(this._sky, this._ground);
        }
        return { sky: this._sky, ground: this._ground! };
    }

    /** a shell coming in: a streak falling onto its growing shadow (under half a second: you see it, you barely dodge it) */
    private _incoming(position: Vector, now: number, landsAt: number, heading: number): void {
        if (landsAt - now < 30) {
            this._impact(position);
            return;
        }
        const { sky, ground } = this._layers();
        const p = toPixiCoords(position);
        const shadow = new Graphics(sharedShapes().shadow);
        shadow.position.set(p.x, p.y);
        shadow.alpha = 0;
        const streak = new Graphics(sharedShapes().streak);
        // they come in slanted, all from about the bomber's direction
        const slant = heading + Math.PI + (Math.random() - 0.5) * 0.5;
        streak.rotation = slant - Math.PI / 2;
        ground.addChild(shadow);
        sky.addChild(streak);
        this._falling.push({ position, start: now, landsAt, shadow, streak, slant });
    }

    /** a shell lands: flash, fireball, blast sound, shake, shockwave, dirt and sparks thrown out, smoke, crater */
    private _impact(position: Vector): void {
        // bodies near it come apart away from it (Blood & gore)
        GoreManager.explosion(position, Layer.Ground, BLAST_RADIUS);
        const me = Game.activePlayer?.position;
        const distance = me ? Vec.len(Vec.sub(me, position)) : 0;
        const floor = MapManager.terrain.getFloor(position, Layer.Ground);
        // (in the water: spray, no crater; on ice (0.98.5, the snow's frozen river): a burst of snow and shards, no crater)
        const ice = floor === FloorNames.Ice;
        const wet = floor === FloorNames.Water || ice;
        if (distance <= EFFECT_RANGE) {
            SoundManager.play("frag_grenade", { position, falloff: 0.4, maxRange: 220, layer: Layer.Ground });
            // the closer, the harder it shakes
            const near = 1 - distance / EFFECT_RANGE;
            CameraManager.shake(distance <= CLOSE_RANGE ? 650 : 420, distance <= CLOSE_RANGE ? 170 * (1 - distance / (CLOSE_RANGE * 1.6)) : 95 * near * near);
            const { sky, ground } = this._layers();
            const p = toPixiCoords(position);
            // a quick fireball, then a dark scorch puff (no lingering haze)
            const fire = new TrenchioSprite("_glow_");
            fire.tint = 0xff8a2a;
            fire.position.set(p.x, p.y);
            fire.scale.set(0.2);
            const scorch = new TrenchioSprite("_glow_");
            scorch.tint = 0x2a2521;
            scorch.position.set(p.x, p.y);
            scorch.scale.set(0.6);
            scorch.alpha = 0;
            ground.addChild(scorch);
            sky.addChild(fire);
            this._blasts.push({ fire, scorch, start: performance.now() });
            const flash = new TrenchioSprite("_glow_");
            flash.tint = 0xffe7b0;
            flash.position.set(p.x, p.y);
            flash.scale.set(2);
            sky.addChild(flash);
            this._flashes.push({ sprite: flash, start: performance.now() });
            const ring = new Graphics(sharedShapes().ring);
            ring.position.set(p.x, p.y);
            ring.scale.set(0.15);
            ground.addChild(ring);
            this._rings.push({ ring, start: performance.now() });
            // dirt (or spray) and sparks thrown outward
            for (let i = 0; i < 7; i++) {
                const angle = Math.random() * Math.PI * 2;
                ParticleManager.spawnParticle({
                    frames: wet && !ice ? "ripple_particle" : "smoke_particle",
                    position: Vec.add(position, Vec.fromPolar(angle, Math.random() * 2)),
                    lifetime: 550 + Math.random() * 450,
                    zIndex: ZIndexes.ObstaclesLayer3 + 0.8,
                    layer: Layer.Ground,
                    scale: { start: 0.35 + Math.random() * 0.25, end: 0.12 },
                    alpha: { start: 1, end: 0 },
                    rotation: { start: Math.random() * 6.28, end: Math.random() * 6.28 },
                    tint: ice ? 0xe8f0f6 : wet ? 0xcfe6f2 : 0x3b2b1c,
                    speed: Vec.fromPolar(angle, 14 + Math.random() * 20)
                });
            }
            for (let i = 0; i < 4; i++) {
                const angle = Math.random() * Math.PI * 2;
                ParticleManager.spawnParticle({
                    frames: "fire_ember",
                    position,
                    lifetime: 280 + Math.random() * 320,
                    zIndex: ZIndexes.ObstaclesLayer3 + 0.9,
                    layer: Layer.Ground,
                    scale: { start: 0.8, end: 0.1 },
                    alpha: { start: 1, end: 0 },
                    speed: Vec.fromPolar(angle, 24 + Math.random() * 26)
                });
            }
            // a little smoke that hangs over the spot for a couple of seconds
            for (let i = 0; i < 2; i++) {
                const angle = Math.random() * Math.PI * 2;
                ParticleManager.spawnParticle({
                    frames: "smoke_particle",
                    position: Vec.add(position, Vec.fromPolar(angle, Math.random() * 3)),
                    lifetime: 1800 + Math.random() * 800,
                    zIndex: ZIndexes.ObstaclesLayer3 + 0.7,
                    layer: Layer.Ground,
                    scale: { start: 1.1 + Math.random() * 0.4, end: 2.8 + Math.random() * 0.6 },
                    alpha: { start: 0.42, end: 0 },
                    rotation: { start: Math.random() * 6.28, end: Math.random() * 6.28 },
                    tint: wet ? 0xb9c4c9 : 0x423d37,
                    speed: Vec.add(Vec.fromPolar(angle, 1.5 + Math.random() * 2.5), Vec(0, -1))
                });
            }
        }
        // a shell in the water leaves no crater
        if (wet) return;
        if (!this._craters) {
            this._craters = new Container();
            this._craters.zIndex = ZIndexes.Decals;
            CameraManager.addObject(this._craters);
        }
        const crater = new TrenchioSprite("explosion_decal");
        const p = toPixiCoords(position);
        crater.position.set(p.x, p.y);
        crater.rotation = Math.random() * Math.PI * 2;
        crater.scale.set(1.6 + Math.random() * 0.45);
        crater.alpha = 0.92;
        this._craters.addChild(crater);
    }

    /** every frame: the zone pulses, decoys wander, whistles start, shells come in, flashes and rings fade */
    update(): void {
        const now = performance.now();
        const frame = Math.min(0.1, Math.max(0, (now - (this._lastFrame ?? now)) / 1000));
        this._lastFrame = now;
        for (const [id, barrage] of this._barrages) {
            if (now >= barrage.endsAt + 1500) {
                this._remove(id);
                continue;
            }
            const left = barrage.landsAt - now;
            barrage.zone.alpha = 0.55 + 0.45 * Math.abs(Math.sin(now / (left > 2000 ? 260 : 110)));
            this._wander(barrage, now, frame);
            for (let i = barrage.whistles.length - 1; i >= 0; i--) {
                const whistle = barrage.whistles[i];
                if (now < whistle.at) continue;
                barrage.whistles.splice(i, 1);
                SoundManager.play("artillery_whistle", { position: whistle.position, falloff: 0.35, maxRange: 220, layer: Layer.Ground });
            }
            const k = (now - barrage.start) / SHADOW_MS;
            if (k >= 0 && k <= 1) {
                const p = toPixiCoords(Vec.add(barrage.center, Vec.fromPolar(barrage.heading, (k - 0.5) * 1400)));
                barrage.shadow.position.set(p.x, p.y);
                barrage.shadow.alpha = 0.34 * Math.min(1, k * 6, (1 - k) * 6);
            } else {
                barrage.shadow.alpha = 0;
            }
        }
        for (let i = this._falling.length - 1; i >= 0; i--) {
            const shell = this._falling[i];
            const t = (now - shell.start) / Math.max(1, shell.landsAt - shell.start);
            if (t >= 1) {
                shell.shadow.destroy();
                shell.streak.destroy();
                this._falling.splice(i, 1);
                this._impact(shell.position);
                continue;
            }
            const eased = t * t;
            shell.shadow.alpha = 0.1 + 0.5 * eased;
            shell.shadow.scale.set(0.35 + 0.65 * eased);
            const height = 620 * (1 - eased);
            const p = toPixiCoords(shell.position);
            shell.streak.position.set(p.x + Math.cos(shell.slant) * height, p.y + Math.sin(shell.slant) * height - height * 0.35);
            shell.streak.scale.set(0.7 + 0.5 * eased);
        }
        for (let i = this._flashes.length - 1; i >= 0; i--) {
            const flash = this._flashes[i];
            const t = (now - flash.start) / FLASH_MS;
            if (t >= 1) {
                flash.sprite.destroy();
                this._flashes.splice(i, 1);
                continue;
            }
            flash.sprite.scale.set(1.6 + 4.4 * t);
            flash.sprite.alpha = 1 - t;
        }
        for (let i = this._blasts.length - 1; i >= 0; i--) {
            const blast = this._blasts[i];
            const age = now - blast.start;
            if (age >= SCORCH_MS) {
                blast.fire.destroy();
                blast.scorch.destroy();
                this._blasts.splice(i, 1);
                continue;
            }
            const f = Math.min(1, age / FIRE_MS);
            // grows fast, then fades
            blast.fire.scale.set(0.2 + 1.5 * (1 - (1 - Math.min(1, age / 220)) ** 3));
            blast.fire.alpha = f >= 1 ? 0 : 1 - f * f;
            const s = age / SCORCH_MS;
            blast.scorch.scale.set(0.6 + 1.5 * Math.sqrt(s));
            blast.scorch.alpha = 0.5 * Math.min(1, age / 200) * (1 - s);
        }
        for (let i = this._rings.length - 1; i >= 0; i--) {
            const ring = this._rings[i];
            const t = (now - ring.start) / RING_MS;
            if (t >= 1) {
                ring.ring.destroy();
                this._rings.splice(i, 1);
                continue;
            }
            ring.ring.scale.set(0.15 + 1.35 * Math.sqrt(t));
            ring.ring.alpha = 0.85 * (1 - t);
        }
        if (this._barrages.size || this._banner?.hidden === false) this._updateBanner(now);
    }

    private _lastFrame?: number;

    /** decoy markers: they drift, dart off, stop, vanish and reappear somewhere else, never where a shell will fall */
    private _wander(barrage: ActiveBarrage, now: number, frame: number): void {
        const fade = now > barrage.endsAt ? Math.max(0, 1 - (now - barrage.endsAt) / 800) : 1;
        for (const decoy of barrage.decoys) {
            if (now >= decoy.nextTurnAt) {
                decoy.nextTurnAt = now + 250 + Math.random() * 450;
                const roll = Math.random();
                if (roll < 0.12) {
                    // gone for a moment, then somewhere else
                    decoy.hiddenUntil = now + 150 + Math.random() * 250;
                    decoy.position = randomIn(barrage.center, barrage.radius * 0.85);
                } else if (roll < 0.3) {
                    decoy.pausedUntil = now + 250 + Math.random() * 350;
                }
                const off = Vec.sub(decoy.position, barrage.center);
                const outside = Vec.len(off) > barrage.radius * 0.8;
                const angle = outside ? Math.atan2(-off.y, -off.x) + (Math.random() - 0.5) * 1.2 : Math.random() * Math.PI * 2;
                decoy.velocity = Vec.fromPolar(angle, 25 + Math.random() * 30);
            }
            if (now >= decoy.pausedUntil) decoy.position = Vec.add(decoy.position, Vec.scale(decoy.velocity, frame));
            const p = toPixiCoords(decoy.position);
            decoy.mark.position.set(p.x, p.y);
            decoy.mark.rotation += frame * 1.5;
            decoy.mark.alpha = now < decoy.hiddenUntil ? 0 : fade * (0.55 + 0.45 * Math.abs(Math.sin(now / 140 + decoy.nextTurnAt)));
        }
    }

    /** a warning for whoever is near a zone (you, or the player you're watching) */
    private _updateBanner(now: number): void {
        const me = Game.activePlayer?.position;
        let soonest: number | undefined;
        for (const barrage of this._barrages.values()) {
            if (now > barrage.endsAt) continue;
            if (me && Vec.len(Vec.sub(me, barrage.center)) <= barrage.radius + 25) soonest = Math.min(soonest ?? Infinity, barrage.landsAt - now);
        }
        if (soonest === undefined) {
            if (this._banner) this._banner.hidden = true;
            return;
        }
        if (!this._banner) {
            this._banner = document.createElement("div");
            this._banner.id = "artillery-banner";
            document.body.appendChild(this._banner);
        }
        this._banner.hidden = false;
        this._banner.textContent = soonest > 0
            ? t("⚠ ARTILLERY INCOMING · <seconds> · get inside a building or out of the red zone", { seconds: Math.ceil(soonest / 1000) })
            : t("⚠ SHELLS LANDING · TAKE COVER");
    }

    private _remove(id: number): void {
        const barrage = this._barrages.get(id);
        if (!barrage) return;
        barrage.container.destroy({ children: true });
        barrage.shadow.destroy();
        this._barrages.delete(id);
        this._updateBanner(performance.now());
    }

    /** a new game, or the view rewound: nothing left over (craters included) */
    reset(): void {
        for (const id of [...this._barrages.keys()]) this._remove(id);
        for (const shell of this._falling.splice(0)) {
            shell.shadow.destroy();
            shell.streak.destroy();
        }
        for (const flash of this._flashes.splice(0)) flash.sprite.destroy();
        for (const ring of this._rings.splice(0)) ring.ring.destroy();
        for (const blast of this._blasts.splice(0)) {
            blast.fire.destroy();
            blast.scorch.destroy();
        }
        this._sky?.destroy({ children: true });
        this._ground?.destroy({ children: true });
        this._sky = this._ground = undefined;
        this._craters?.destroy({ children: true });
        this._craters = undefined;
        if (this._banner) this._banner.hidden = true;
    }
}

export const Artillery = new ArtilleryClass();
